import { AlertCircle, CircleHelp } from 'lucide-react'
import { severityLabel } from '@/lib/siteHealth'
import type { SiteHealthFindingEvidence } from '@/lib/siteHealth.types'
import FixGuidance from './FixGuidance'
import RobotsPolicyMatrix from './RobotsPolicyMatrix'
import { EYEBROW_CLASS, severityBadgeClass } from './SiteHealthStyles'
import type { PageFindingDetailsProps } from './siteHealthComponents.types'
import TextDiffDialog from './TextDiffDialog'

function evidenceSummary(evidence: SiteHealthFindingEvidence): string | null {
  const candidates = [evidence.summary, evidence.actual, evidence.expected]

  return (
    candidates.find(
      (candidate): candidate is string =>
        typeof candidate === 'string' && candidate.trim().length > 0
    ) ?? null
  )
}

export default function PageFindingDetails({ page }: PageFindingDetailsProps) {
  return (
    <section aria-label={`Findings for ${page.url}`} className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className={EYEBROW_CLASS}>Findings on this page</h3>
        {page.textDiff && (
          <TextDiffDialog evidence={page.textDiff} pageId={page.id} />
        )}
      </div>
      {page.findings.length === 0 && (
        <div className="rounded-xl border border-dashed border-border bg-muted/20 p-5 text-sm text-muted-foreground">
          No findings were returned for this page.
        </div>
      )}
      <div className="space-y-3">
        {page.findings.map((finding) => {
          const summary = evidenceSummary(finding.evidence)
          const notEvaluated = finding.evaluationState === 'not_evaluated'

          return (
            <article
              key={finding.id}
              className="rounded-xl border border-border bg-card p-4"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className={severityBadgeClass(finding.severity)}>
                  {severityLabel(finding.severity)}
                </span>
                {notEvaluated && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    <CircleHelp className="size-3.5" aria-hidden="true" />
                    Not evaluated
                  </span>
                )}
                <h4 className="font-medium text-foreground">{finding.title}</h4>
              </div>
              {summary && (
                <p className="mt-3 text-sm leading-6 text-muted-foreground">
                  {summary}
                </p>
              )}
              <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <div>
                  <dt className="inline text-muted-foreground">Impact: </dt>
                  <dd className="inline capitalize">{finding.impact}</dd>
                </div>
                <div>
                  <dt className="inline text-muted-foreground">Confidence: </dt>
                  <dd className="inline capitalize">{finding.confidence}</dd>
                </div>
              </dl>
              {notEvaluated && (
                <p className="mt-3 flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
                  <AlertCircle
                    className="mt-0.5 size-4 shrink-0"
                    aria-hidden="true"
                  />
                  Render evidence was unavailable, so this check was not
                  evaluated. It is not counted as a passing rule.
                </p>
              )}
              {finding.evidence.robotsPolicy && (
                <div className="mt-3">
                  <RobotsPolicyMatrix policy={finding.evidence.robotsPolicy} />
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-border/70 pt-4">
                <FixGuidance
                  guidance={finding}
                  subject={{ findingId: finding.id }}
                />
                <TextDiffDialog
                  evidence={finding.evidence}
                  findingId={finding.id}
                />
              </div>
            </article>
          )
        })}
      </div>
    </section>
  )
}
